import React, { useMemo, useState } from 'react';
import { View, StyleSheet, ScrollView, Pressable } from 'react-native';
import {
  Bevel,
  Button,
  MenuBar,
  RunningMan,
  Text,
  TextField,
  Window,
  colors,
  makeStyles,
  useTheme,
} from '@ogaim/ui';
import { effectivePresence, type Presence } from '@ogaim/core';
import { useStore, groupBuddies } from '../store';

export interface BuddyListScreenProps {
  /** Desktop renders IM windows alongside; iOS pushes them, so it hides chrome. */
  onOpenSettings?: () => void;
  /** Opens the away-message editor. Falls back to a canned message if absent. */
  onEditAwayMessage?: () => void;
  /**
   * Whether to draw the Win95 window frame. The web desktop supplies its own
   * draggable frame, so it renders this frameless. The menu bar and status bar
   * stay either way — in Win95 those live inside the frame, not on it.
   */
  frame?: boolean;
}

export function BuddyListScreen({
  onOpenSettings,
  onEditAwayMessage,
  frame = true,
}: BuddyListScreenProps = {}) {
  const theme = useTheme();
  const styles = useStyles();
  const me = useStore((s) => s.me);
  const buddies = useStore((s) => s.buddies);
  const conversations = useStore((s) => s.conversations);
  const openConversation = useStore((s) => s.openConversation);
  const refreshBuddies = useStore((s) => s.refreshBuddies);
  const addBuddy = useStore((s) => s.addBuddy);
  const signOff = useStore((s) => s.signOff);
  const setPresence = useStore((s) => s.setPresence);
  const connection = useStore((s) => s.connection);

  const [adding, setAdding] = useState(false);
  const [newBuddy, setNewBuddy] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // `buddies` is a stable reference from the store; the grouping is derived
  // here so it is recomputed only when the list actually changes.
  const groups = useMemo(() => groupBuddies(buddies), [buddies]);
  const totalOnline = useMemo(() => groups.reduce((n, g) => n + g.online, 0), [groups]);

  const unreadFor = (screenName: string) =>
    conversations[screenName.replace(/\s/g, '').toLowerCase()]?.unread ?? 0;

  const submitAdd = async () => {
    const name = newBuddy.trim();
    if (!name) return;
    try {
      await addBuddy(name);
      setNewBuddy('');
      setAdding(false);
      setAddError(null);
    } catch (err: any) {
      setAddError(err?.message ?? 'Could not add that buddy.');
    }
  };

  const away = me?.presence === 'AWAY';

  const menu = (
        <MenuBar
          items={[
            { label: 'My AIM', onPress: onOpenSettings },
            { label: 'People', onPress: () => setAdding((v) => !v) },
            { label: 'Help' },
          ]}
        />
  );

  const statusBar = (
        <View style={styles.statusRow}>
          <Text disabled style={styles.statusText}>
            {connection === 'ready'
              ? `${totalOnline} of ${groups.reduce((n, g) => n + g.buddies.length, 0)} online`
              : connection === 'connecting'
                ? 'Reconnecting…'
                : 'Disconnected'}
          </Text>
          <View
            style={[
              styles.connectionDot,
              { backgroundColor: connection === 'ready' ? '#008000' : '#808000' },
            ]}
          />
        </View>
  );

  const body = (
    <>
      {adding ? (
        <Bevel variant="raised" style={styles.addPanel} contentStyle={styles.addPanelInner}>
          <Text style={styles.addLabel}>Add a buddy by screen name:</Text>
          <View style={styles.addRow}>
            <TextField
              value={newBuddy}
              onChangeText={(t) => {
                setNewBuddy(t);
                setAddError(null);
              }}
              onSubmitEditing={submitAdd}
              placeholder="ScreenName"
              containerStyle={styles.addField}
              maxLength={16}
              accessibilityLabel="Buddy screen name"
            />
            <Button title="Add" onPress={submitAdd} disabled={!newBuddy.trim()} />
          </View>
          {addError ? <Text style={styles.addError}>{addError}</Text> : null}
        </Bevel>
      ) : null}

      <Bevel variant="sunken" background={theme.colors.fieldBg} style={styles.list}>
        <ScrollView contentContainerStyle={styles.listContent}>
          {groups.length === 0 ? (
            <View style={styles.empty}>
              <Text disabled style={styles.emptyText}>
                Your buddy list is empty.
              </Text>
              <Text disabled style={styles.emptyText}>
                Choose People ▸ to add someone.
              </Text>
            </View>
          ) : (
            groups.map((group) => (
              <View key={group.name}>
                <Pressable
                  onPress={() =>
                    setCollapsed((c) => ({ ...c, [group.name]: !c[group.name] }))
                  }
                  accessibilityRole="button"
                  accessibilityLabel={`${group.name}, ${group.online} of ${group.buddies.length} online`}
                  style={styles.groupHeader}
                >
                  <Text bold>
                    {collapsed[group.name] ? '▸' : '▾'} {group.name} ({group.online}/
                    {group.buddies.length})
                  </Text>
                </Pressable>

                {collapsed[group.name]
                  ? null
                  : group.buddies.map((buddy) => (
                      <BuddyRow
                        key={buddy.screenName}
                        screenName={buddy.screenName}
                        presence={buddy.effective}
                        awayMessage={buddy.awayMessage}
                        unread={unreadFor(buddy.screenName)}
                        onPress={() => openConversation(buddy.screenName)}
                      />
                    ))}
              </View>
            ))
          )}
        </ScrollView>
      </Bevel>

      <View style={styles.footer}>
        <Button
          title={away ? "I'm Back" : 'Away'}
          onPress={() => {
            // Coming back needs no editor; going away opens one when the host
            // provides it, so the message can be written rather than assumed.
            if (away) void setPresence('ONLINE', null);
            else if (onEditAwayMessage) onEditAwayMessage();
            else void setPresence('AWAY', 'I am away from my computer right now.');
          }}
          style={styles.footerButton}
        />
        <Button title="Refresh" onPress={() => void refreshBuddies()} style={styles.footerButton} />
        <Button title="Sign Off" onPress={signOff} style={styles.footerButton} />
      </View>
    </>
  );

  if (!frame) {
    return (
      <View style={styles.frameless}>
        {menu}
        <View style={styles.framelessBody}>{body}</View>
        <Bevel variant="sunken" style={styles.framelessStatus} contentStyle={styles.framelessStatusInner}>
          {statusBar}
        </Bevel>
      </View>
    );
  }

  return (
    <Window
      title={me ? `${me.screenName}'s Buddy List` : 'Buddy List'}
      icon={<RunningMan size={14} />}
      menu={menu}
      statusBar={statusBar}
    >
      {body}
    </Window>
  );
}

function BuddyRow({
  screenName,
  presence,
  awayMessage,
  unread,
  onPress,
}: {
  screenName: string;
  presence: Presence;
  awayMessage: string | null;
  unread: number;
  onPress: () => void;
}) {
  const theme = useTheme();
  const styles = useStyles();
  const [held, setHeld] = useState(false);
  const offline = presence === 'OFFLINE';

  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => setHeld(true)}
      onPressOut={() => setHeld(false)}
      accessibilityRole="button"
      accessibilityLabel={`${screenName}, ${presenceLabel(presence)}${unread ? `, ${unread} unread` : ''}`}
      style={[styles.buddyRow, held && styles.buddyRowSelected]}
    >
      <View style={styles.buddyIcon}>
        {/* Offline buddies were hidden entirely in AIM; here they stay visible
            but greyed, which reads better on a small invite-only list. */}
        <RunningMan size={12} color={presenceColor(presence, theme)} />
      </View>
      <Text
        numberOfLines={1}
        style={[
          styles.buddyName,
          held && styles.buddyTextSelected,
          offline && styles.buddyOffline,
          presence === 'IDLE' && styles.buddyIdle,
        ]}
      >
        {screenName}
        {presence === 'IDLE' ? ' (Idle)' : ''}
      </Text>
      {unread > 0 ? (
        <Bevel variant="raised" background={theme.colors.accent} style={styles.badge} contentStyle={styles.badgeInner}>
          <Text style={styles.badgeText}>{unread > 9 ? '9+' : unread}</Text>
        </Bevel>
      ) : presence === 'AWAY' ? (
        <Text disabled style={styles.awayTag} numberOfLines={1}>
          {awayMessage ? 'Away' : 'Away'}
        </Text>
      ) : null}
    </Pressable>
  );
}

const presenceLabel = (p: Presence) =>
  p === 'ONLINE' ? 'online' : p === 'AWAY' ? 'away' : p === 'IDLE' ? 'idle' : 'offline';

/** Presence tints, derived from the theme so they suit each palette. */
function presenceColor(p: Presence, t: ReturnType<typeof useTheme>) {
  if (p === 'ONLINE') return t.colors.accent;
  if (p === 'AWAY') return t.colors.awayText;
  if (p === 'IDLE') return t.colors.idleText;
  return t.colors.disabledText;
}

const useStyles = makeStyles((t) => ({
  addPanel: { marginBottom: 3 },
  addPanelInner: { padding: 6, gap: 4 },
  addLabel: {},
  addRow: { flexDirection: 'row', gap: 6, alignItems: 'center' },
  addField: { flex: 1 },
  addError: { color: '#800000' },

  list: { flex: 1 },
  listContent: { paddingVertical: 2 },

  groupHeader: { paddingHorizontal: 4, paddingVertical: 3 },

  buddyRow: { flexDirection: 'row', alignItems: 'center', paddingLeft: 16, paddingRight: 4, paddingVertical: 3, gap: 5 },
  buddyRowSelected: { backgroundColor: t.colors.selectionBg },
  buddyIcon: { width: 12, alignItems: 'center' },
  buddyName: { flex: 1 },
  buddyTextSelected: { color: t.colors.selectionText },
  buddyOffline: { color: t.colors.disabledText },
  buddyIdle: { color: t.colors.idleText },
  awayTag: { fontSize: 10 },
  badge: { minWidth: 16, height: 14 },
  badgeInner: { alignItems: 'center', justifyContent: 'center', paddingHorizontal: 3 },
  badgeText: { fontSize: 10, fontWeight: 'bold' },

  empty: { padding: 12, gap: 3, alignItems: 'center' },
  emptyText: { textAlign: 'center' },

  footer: { flexDirection: 'row', gap: 3, marginTop: 3 },
  footerButton: { flex: 1 },

  // Frameless: the host supplies the window frame, so this only lays out the
  // interior — menu bar, content, and the sunken status well along the bottom.
  frameless: { flex: 1, backgroundColor: t.colors.face, padding: 3 },
  framelessBody: { flex: 1, minHeight: 0, marginTop: 3 },
  framelessStatus: { marginTop: 3, height: 20 },
  framelessStatusInner: { justifyContent: 'center', paddingHorizontal: 4 },

  statusRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  statusText: { flex: 1, fontSize: 10 },
  connectionDot: { width: 7, height: 7 },
}));
